import React, {useEffect, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {AccessControlIndicator, defineAccessControlledResource, type Resource} from '@igp/shared/access-control';
import {ErrorModal} from '@igp/shared/components/error';
import type {FilterWithPlacement} from '@igp/shared/components/filter';
import {Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {
    availableTransactionFilterValues,
    DataGridTransactionsServer,
    nonHistoryTransactionTimestampMin,
    TransactionDownloadCsvButton,
    TransactionQueryFilters,
    type TransactionViewModelKeys,
    useTransactions,
    useTransactionSettings,
} from '@igp/shared/features/transaction';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {extendedViewCleanDelay, type ViewType} from '@igp/shared/redux/view';
import {EntityType} from '@models';

import {withAccessCheck} from '@access-control';
import {agentPolicyRule} from '@policy-access';
import {useAgentId} from '@features/account/hooks';
import {transactionServiceTypes} from '@features/transaction';

import {HomePageLayout, noPermissionLocalization} from './HomePageLayout';

const localized = defineMessages({
    title: {
        id: 'TransactionHistory_title',
        defaultMessage: 'Transaction History',
    },
});

function TransactionHistoryInner() {
    const viewType: ViewType = 'TransactionHistoryPage';
    const agentId = useAgentId();
    const keys: TransactionViewModelKeys[] = [
        'transaction_updated_ts',
        'transaction_type',
        'payment_method_name',
        'payment_method_description',
        'amount_without_currency',
        'currency',
        'transaction_status',
        'current_balance',
        'current_casino_coin_balance',
        'ptp_counterparty_uid',
    ];
    const {
        items,
        rawItems,
        summary,
        summaryRaw,
        totalCount,
        searchFilter,
        filterString,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleFilterChange,
    } = useTransactions<EntityType>({
        viewType,
        showTotal: true,
        displayName: localized.title,
        fields: keys,
        defaultPaging: {page: 1, pageSize: 25},
        defaultFilters: [
            {key: 'uid', value: agentId},
            {key: 'defaultTransactionTypes', value: availableTransactionFilterValues},
            {key: 'startedTs.from', value: nonHistoryTransactionTimestampMin},
            {key: 'startedTs.to', value: 'now'},
        ],
        defaultSorting: 'updated',
        validateFilter: filter => filter?.find(f => f.key === 'uid')?.value?.toString() === agentId,
        transactionEntityType: EntityType.Transaction,
        syncWithUrl: true,
        cleanDelay: extendedViewCleanDelay,
    });

    const {formatMessage} = useIntl();
    const pinnedColumns: (typeof keys)[number][] = ['transaction_updated_ts', 'transaction_type', 'amount_without_currency'];
    const {visibleColumns, onVisibleColumnsChange} = useTransactionSettings({
        settingId: viewType,
        defaultColumns: keys,
        pinnedColumns,
    });

    const [primaryNode, setPrimaryNode] = useState<Omit<FilterWithPlacement, 'filterName'>>();
    const [secondaryNode, setSecondaryNode] = useState<Omit<FilterWithPlacement, 'filterName'>>();
    const [thirdNode, setThirdNode] = useState<Omit<FilterWithPlacement, 'filterName'>>();

    const primaryRef = useRef<HTMLDivElement>(undefined);
    const secondaryRef = useRef<HTMLDivElement>(undefined);
    const thirdRef = useRef<HTMLDivElement>(undefined);

    useEffect(() => {
        if (primaryRef.current) {
            setPrimaryNode({nodeId: 'primary', node: primaryRef.current});
        }
        if (secondaryRef.current) {
            setSecondaryNode({nodeId: 'secondary', node: secondaryRef.current});
        }
        if (thirdRef.current) {
            setThirdNode({nodeId: 'third', node: thirdRef.current});
        }
    }, []);
    return (
        <>
            <HomePageLayout
                toolbar={
                    <>
                        <Toolbar>
                            <ToolbarItemsContainer primary>
                                <ToolbarItems ref={primaryRef} />
                                <ToolbarItems primary ref={secondaryRef} />
                            </ToolbarItemsContainer>
                            <ToolbarItemsContainer>
                                <ToolbarItems streched>
                                    <ToolbarItems ref={thirdRef} />
                                    <TransactionDownloadCsvButton
                                        keys={visibleColumns}
                                        filterString={filterString}
                                        filename={formatMessage(localized.title)}
                                        showTotal
                                        mode="server"
                                        downloadServerStrategyId={transactionServiceTypes.downloadServerStrategy}
                                        total={totalCount}
                                    />
                                </ToolbarItems>
                            </ToolbarItemsContainer>
                        </Toolbar>
                        <TransactionQueryFilters
                            model={filterString}
                            onChange={handleFilterChange}
                            availableFilters={[
                                {filterName: 'transactionHistoryTypes', ...primaryNode},
                                {filterName: 'paymentMethod', ...secondaryNode},
                                {filterName: 'amount', ...secondaryNode},
                                {filterName: 'status', ...secondaryNode},
                                {filterName: 'historicalCreationDate', ...thirdNode},
                            ]}
                        />
                    </>
                }
                body={
                    <DataGridTransactionsServer
                        hasColumnSelection
                        visibleColumns={visibleColumns}
                        pinnedColumns={pinnedColumns}
                        onColumnsVisibilityUpdate={onVisibleColumnsChange}
                        columns={keys}
                        rows={items}
                        rawModels={rawItems}
                        pinnedRow={summary}
                        pinnedRawModel={summaryRaw}
                        rowCount={totalCount}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSortModelChange={handleSortChange}
                        page={searchFilter?.paging?.page}
                        pageSize={searchFilter?.paging?.pageSize}
                        sortModel={searchFilter?.sorting}
                        isFlexible
                    />
                }
            />
        </>
    );
}

const transactionHistoryResource: Resource = defineAccessControlledResource({
    name: 'TransactionHistoryPage',
    rules: agentPolicyRule,
});

const TransactionHistoryWithAccess = withAccessCheck(
    TransactionHistoryInner,
    transactionHistoryResource,
    AccessControlIndicator.InfoMessage,
    noPermissionLocalization
);

export const TransactionHistory = withTraceErrorBoundary(TransactionHistoryWithAccess, ErrorModal);
